import { useEffect, useState } from 'react';
import styles from './IdentityVerification.module.css';
import nameImg from '../../../assets/pages/addProperty/identity-verification/nameImg.svg';
import faceImg from '../../../assets/pages/addProperty/identity-verification/faceImg.svg';
import idImg from '../../../assets/pages/addProperty/identity-verification/idImg.svg';
import doneImg from '../../../assets/pages/addProperty/identity-verification/doneImg.svg';
import { useModal } from '../../hooks/modalContext';
import { getIDVStatus, getTenantDetails } from '../../../auth/_requests';
import { useAuth } from '../../../auth/Auth';
import { pushStorage } from '../../webPush/pushNotificationStorage';

type Step =
  | 'pending-screen-1'
  | 'pending-screen-2'
  | 'pending-screen-3'
  | 'pending-final'
  | 'verification-done'
  | 'pending-finished';

const stepTexts: Record<Step, string> = {
  'pending-screen-1': 'Verifying name',
  'pending-screen-2': 'Verifying face',
  'pending-screen-3': 'Verifying ID',
  'pending-final': 'Almost there... Final verification in progress',
  'verification-done': 'Verification done!',
  'pending-finished':
    'Your identity verification is still pending. We’ll notify you once it’s complete.',
};

const stepImages: Record<Step, string> = {
  'pending-screen-1': nameImg,
  'pending-screen-2': faceImg,
  'pending-screen-3': idImg,
  'pending-final': faceImg,
  'verification-done': doneImg,
  'pending-finished': faceImg,
};

const PendingScreen = () => {
  const { setModal, modalState } = useModal();
  const { auth, currentUser } = useAuth();
  const userId = currentUser?.user?.userId;
  const idvStatus = modalState?.idvStatus;
  const [currentStep, setCurrentStep] = useState<Step>('pending-screen-1');
  useEffect(() => {
    if (idvStatus === 'success') return;

    let timer: NodeJS.Timeout;

    const stepFlow = () => {
      switch (currentStep) {
        case 'pending-screen-1':
          timer = setTimeout(() => setCurrentStep('pending-screen-2'), 2000);
          break;
        case 'pending-screen-2':
          timer = setTimeout(() => setCurrentStep('pending-screen-3'), 2000);
          break;
        case 'pending-screen-3':
          timer = setTimeout(() => setCurrentStep('pending-final'), 2000);
          break;
        case 'pending-final':
          timer = setTimeout(async () => {
            try {
              const [res, tenantRes] = await Promise.all([
                getIDVStatus(auth?.data.token).catch(() => null),
                getTenantDetails(auth?.data.token).catch(() => null),
              ]);

              const tenant = tenantRes?.data?.tenant;
              const tenantIdv = tenant?.tenantIdv;
              const { status, tenantIdvStatus } = res?.data ?? {};

              const isApproved =
                tenant?.isIdentityVerified === 1 ||
                (tenant?.isIdentityVerified as unknown) === true ||
                tenantIdv?.status === 'approved' ||
                status === 'AUTO_APPROVED' ||
                status === 'MANUAL_APPROVED' ||
                status === 'approved' ||
                tenantIdvStatus === 'approved';

              if (isApproved) {
                if (userId != null) {
                  pushStorage.setHasCompletedIdv(userId);
                }
                setCurrentStep('verification-done');
                setTimeout(() => {
                  setModal('idv-verification-success', {
                    tenantBuildingId: modalState?.tenantBuildingId,
                    pendingIdvAuthHold: modalState?.pendingIdvAuthHold,
                  });
                }, 2000);
              } else {
                setCurrentStep('pending-finished');
              }
            } catch {
              setCurrentStep('pending-finished');
            }
          }, 3000);
          break;
        default:
          break;
      }
    };

    stepFlow();

    return () => clearTimeout(timer);
  }, [
    currentStep,
    idvStatus,
    auth?.data.token,
    setModal,
    modalState?.tenantBuildingId,
    modalState?.pendingIdvAuthHold,
    userId,
  ]);

  useEffect(() => {
    if (idvStatus === 'success') {
      if (userId != null) {
        pushStorage.setHasCompletedIdv(userId);
      }
      setCurrentStep('verification-done');
      const timer = setTimeout(() => {
        setModal('idv-verification-success', {
          tenantBuildingId: modalState?.tenantBuildingId,
          pendingIdvAuthHold: modalState?.pendingIdvAuthHold,
        });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [idvStatus, setModal, modalState?.tenantBuildingId, modalState?.pendingIdvAuthHold, userId]);

  const getProgressWidth = () => {
    switch (currentStep) {
      case 'pending-screen-1':
        return '5%';
      case 'pending-screen-2':
        return '35%';
      case 'pending-screen-3':
        return '70%';
      case 'pending-final':
        return '85%';
      case 'verification-done':
      case 'pending-finished':
        return '100%';
    }
  };

  return (
    <main className={`${styles.main} ${styles.centeredMain}`}>
      <img src={stepImages[currentStep]} alt='Verification Step' className={styles.img} />
      <div className={styles.progressContainer}>
        <div className={styles.progressTrack}>
          <div className={styles.progressFill} style={{ width: getProgressWidth() }} />
        </div>
      </div>
      <p className={styles.text}>{stepTexts[currentStep]}</p>
      <button
        className={`cancelBtn ${styles.cancelBtn}`}
        onClick={() =>
          setModal(null, {
            refreshProperties: true,
            refreshProfile: true,
          })
        }
      >
        {currentStep === 'pending-finished' ? 'Close' : 'Cancel'}
      </button>
    </main>
  );
};

export default PendingScreen;
